//uuid v4
import { chain } from 'obliterator';
import { useEffect } from 'react';
import type { Observer, Unsubscribable, JsonValue } from 'type-fest';

import type { StructuredCloneable } from '@frontend/utils';
import { noop } from '@frontend/utils';

import { LOGGING } from 'constants/all';

import { uuid } from 'utils/stringUtils';

export type BroadCastMessage<
  P extends StructuredCloneable,
  C extends JsonValue,
> = {
  from: string; //sessionId message is from
  to?: string; //sessionId message is to
  action: string; //aka topic (must be defined)
  context?: C; //context the action is applicable to
  payload?: P; //aka message
};

/*eslint-disable @typescript-eslint/no-explicit-any*/
export type BroadcastObserver<
  P extends StructuredCloneable = any,
  C extends JsonValue = any,
> = Partial<Observer<Omit<BroadCastMessage<P, C>, 'to'>>>;
/*eslint-enable @typescript-eslint/no-explicit-any*/

const helloAction = '__broadcastHello__' as const;
const goodbyeAction = '__broadcastGoodbye__' as const;
export const infoAction = '__broadcastInfo__' as const;

/**
broadcastService allows dispatching messages to other paperlessparts browser tabs/windows, and can subscribe to and re-dispatch applicable actions to redux
@remarks a singleton for each browser tab/window
@remarks the initial use case is to broadcast messages to other tabs/windows that will subscribe to and dispatch applicable actions to redux (provided context is right). But this should be replaced with websockets.
*/
export const broadcastService = new (class {
  public readonly sessionId: string = uuid(); //identifier for this instance of the browser tab/window (used for `from` and `to` in `BroadCastMessage<T>`)
  private readonly channel: BroadcastChannel | undefined;
  private readonly _peers = new Set<string>();
  private subscriptions = new Map<string | null, Set<BroadcastObserver>>();

  private defaultObservers = new Map<string | null, BroadcastObserver>([
    [
      helloAction,
      {
        next: ({ action, from }) => {
          //When a helloAction is received, dispatch hello back (so it knows about us) and add it to known peers
          //Note: multiple hello's are possible if multiple windows/tabs open at the same time and they say hello before receiving hello. (They will stop once peer is known.)
          if (!this._peers.has(from)) {
            this._peers.add(from);
            this.dispatch({ action, to: from });
          }
        },
      },
    ],
    [
      goodbyeAction,
      {
        //when a goodbyeAction is received, remove the peer from the known peers
        next: ({ from }) => {
          this._peers.delete(from);
        },
      },
    ],
    [
      infoAction,
      {
        next: ({ from, context, payload }) => {
          //payload should always be specified
          //eslint-disable-next-line no-console
          console.info(
            ...[
              `Broadcast received info from: ${from},`,
              ...(context != null ? [' Context ', context] : []),
              ...(payload != null ? [', ', payload] : []),
            ],
          );
        },
      },
    ],
    //When LOGGING is true, log all broadcast messages
    ...(LOGGING
      ? [
          [
            null,
            {
              next: ({ from, context, action, payload }) => {
                //eslint-disable-next-line no-console
                console.info(
                  ...[
                    `Broadcast received from: ${from},`,
                    ` Action: ${action}${payload != null ? ',' : ''}`,
                    ...(context != null ? [' Context ', context] : []),
                    ...(payload != null ? [', ', payload] : []),
                  ],
                );
              },
            },
          ] as [null, BroadcastObserver],
        ]
      : []),
  ]);

  constructor() {
    if (
      Object.prototype.hasOwnProperty.call(
        //globalThis is defined in modern browsers and we have fallbacks
        //eslint-disable-next-line no-undef
        globalThis || self || window,
        'BroadcastChannel',
      )
    ) {
      this.channel = new BroadcastChannel('paperless');
      this.channel.onmessage = (e) => this.handle(e);
      window.addEventListener('beforeunload', () => this.dispose());
      this.addDefaultSubscriptions();
      //eslint-disable-next-line no-console
      console.info(`Broadcast Service for browser ${this.sessionId} is up`);

      this.dispatch({ action: helloAction }); //announce self to others on the 'paperless' BroadcastChannel
    }
  }

  public reset() {
    this.clearAllSubscriptions();
    this.addDefaultSubscriptions();
  }

  /**
  Dispose
  */
  public dispose() {
    try {
      this.dispatch({ action: goodbyeAction }); //say goodbye so others can clean up their peers
    } catch (e) {
      //best efforts made to tell peers goodbye
    }
    this._peers.clear();
    this.clearAllSubscriptions();
    try {
      if (this.channel) {
        this.channel.close();
        this.channel.onmessage = null;
      }
    } catch (e) {
      //best efforts made to close
    }
    this.defaultObservers.clear(); //no need given channel is closed
  }

  /**
  Iterable of the of peer tabs/windows session ids that are being observed
  @remarks: These are the sessionIds that each browser's tab/window defines in its instance of the paperless parts broadcastService
  */
  public get peers(): Iterable<string> {
    const peers = this._peers;
    return {
      *[Symbol.iterator]() {
        yield* peers;
      },
    };
  }

  /**
  Count of peer tabs/windows that are being observed
  */
  public get peersCount(): number {
    return this._peers.size;
  }

  /**
  Iterable of the subscribed actions
  @remarks `null` is a subscription to ALL actions
  */
  public get subscribedActions(): Iterable<string | null> {
    const subscriptions = this.subscriptions;
    return {
      [Symbol.iterator]() {
        return subscriptions.keys();
      },
    };
  }

  /**
  Count of subscriptions
  */
  public get subscriptionsCount(): number {
    let count = 0;
    for (const subscriptionsForAction of this.subscriptions.values()) {
      count += subscriptionsForAction.size;
    }
    return count;
  }

  /**
  Subscribe to some observer to an action that may be received by other browser tab/window
  @remarks when `action === null`, the observer is subscribed to ALL actions
  */
  public subscribe(
    action: string | null,
    observer: BroadcastObserver,
  ): Unsubscribable {
    if (!this.channel) {
      //BroadcastChannel is not supported (so don't create a real subscription)
      return {
        unsubscribe: noop,
      };
    }
    let subscriptionsForAction = this.subscriptions.get(
      action,
    ) as Set<BroadcastObserver>;
    if (!subscriptionsForAction) {
      subscriptionsForAction = new Set();
      this.subscriptions.set(action, subscriptionsForAction);
    }
    subscriptionsForAction.add(observer);
    return {
      unsubscribe: () => subscriptionsForAction.delete(observer),
    };
  }

  /**
  Unsubscribe some observer to some action from another browser tab/window
  @remarks when `action === null`, the observe is being unsubscribed from ALL actions
  */
  public unsubscribe(
    observer: BroadcastObserver,
    action: string | null = null,
  ): boolean {
    const subscriptionsForAction = this.subscriptions.get(
      action,
    ) as Set<BroadcastObserver>;
    let deleted = false;
    if (subscriptionsForAction) {
      deleted = subscriptionsForAction.delete(observer);
      if (subscriptionsForAction.size === 0) {
        this.subscriptions.delete(action);
      }
    }
    return deleted;
  }

  /**
  Clears all subscriptions for a specific action
  @remarks: the `null` action is a category for observers that observe ANY action
  */
  public clearSubscriptions(action: string | null = null) {
    const subscriptionsForAction = this.subscriptions.get(action);
    if (subscriptionsForAction) {
      subscriptionsForAction.clear();
      this.subscriptions.delete(action);
    }
  }

  /**
  Clears all subscriptions for all actions
  */
  public clearAllSubscriptions() {
    for (const subscriptionsForAction of this.subscriptions.values()) {
      subscriptionsForAction.clear();
    }
    this.subscriptions.clear();
  }

  /**
  Dispatches an action and payload to other paperlessparts browser tabs/windows (or to a specific browser tab/window using `to`)
  @remarks: Other browsers can subscribe/observe this action (or all actions) that are dispatched
  */
  public dispatch<P extends StructuredCloneable, C extends JsonValue>(
    actionOrOptions:
      | string
      | { action: string; context?: C; payload?: P; to?: string },
    _payload?: P,
  ) {
    if (!this.channel) {
      return; //BroadcastChannel is not supported
    }
    const { action, context, payload, to } =
      typeof actionOrOptions === 'string'
        ? {
            action: actionOrOptions,
            context: undefined,
            payload: _payload,
            to: null,
          }
        : {
            ...actionOrOptions,
            ...(_payload != null && { payload: _payload }),
          };

    if (to && !this._peers.has(to)) {
      return; //don't waste resources of other tabs/windows by broadcasting to unknown peer
    }

    try {
      this.channel.postMessage({
        ...(to != null && { to }),
        from: this.sessionId,
        action,
        ...(context && { context }),
        ...(payload != null && { payload }),
      } as BroadCastMessage<P, C>);

      if (LOGGING) {
        //eslint-disable-next-line no-console
        console.info(
          ...[
            `Broadcast sent${to ? ` to: ${to}` : ''}:`,
            ` Action: ${action}${context != null ? ',' : ''}`,
            ...(context != null ? [' Context ', context] : []),
            ...(payload != null ? [', ', payload] : []),
          ],
        );
      }
    } catch (e) {
      console.error(e);
    }
  }

  private handle<P extends StructuredCloneable, C extends JsonValue>(
    event: MessageEvent<BroadCastMessage<P, C>>,
  ) {
    const { action, context, from, to, payload } = event.data;
    if (
      action == null ||
      !event.isTrusted ||
      (to != null && to !== this.sessionId) ||
      from === this.sessionId //messages don't arrive from self. But just in case
    ) {
      return;
    }
    const anyActionSubscriptions = this.subscriptions.get(null);
    const subscriptions = this.subscriptions.get(action);
    const value: Omit<BroadCastMessage<P, C>, 'to'> = {
      action,
      context,
      from,
      ...(payload && { payload }),
    };
    for (const subscription of chain(
      subscriptions ?? [],
      anyActionSubscriptions ?? [],
    )) {
      const next = subscription?.next;
      if (typeof next === 'function') {
        //Considering handling subscription callbacks in separate microtasks so we don't block UI if in aggreated they tak too long (ie > 50ms)
        //queueMicrotask(() => { });
        try {
          next(value);
        } catch (err) {
          const error = subscription?.error;
          if (typeof error === 'function') {
            try {
              error(err);
            } catch {
              //noop: Below already loosing right subscription given next() threw
            }
          }
          //loose right to subscription if next() throws
          anyActionSubscriptions?.delete(subscription);
          subscriptions?.delete(subscription);
        }
      }
    }
  }

  private addDefaultSubscriptions() {
    for (const [action, observer] of this.defaultObservers) {
      this.subscribe(action, observer);
    }
  }
})();

/**
useBroadcast hook to subscribe to messages from other browser tabs/windows. Be sure to pass in rebindDeps!
@remarks As with any hook, can only be called inside the body of a function component.
*/
/*eslint-disable @typescript-eslint/no-explicit-any*/
export const useSubscribeBroadcast = <
  P extends StructuredCloneable = any,
  C extends JsonValue = any,
>(
  /*eslint-enable @typescript-eslint/no-explicit-any*/
  action: string | null,
  { next, error }: BroadcastObserver<P, C>,
  rebindDeps: ReadonlyArray<
    | string
    | number
    | boolean
    | undefined
    | null
    | Array<string | number | boolean | undefined | null>
  > = [],
) => {
  //eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/rules-of-hooks
  useEffect(() => {
    const { unsubscribe } = broadcastService.subscribe(action, {
      next,
      ...(error && { error }),
    });
    return () => {
      unsubscribe();
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/rules-of-hooks
  }, [action, ...rebindDeps]);
};
